// @ts-check

/**
 * @file Renders the project create/edit form screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Switch, View} from "react-native-propforge"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormSelect from "../../components/form/form-select"
import FormTextInput from "../../components/form/form-text-input"
import OrganizationsSelect from "../../components/organizations/select"
import {Organization} from "../../frontend-models/organization"
import {Project} from "../../frontend-models/project"
import {optionalRouteParam} from "../../shared/route-params"
import {useScreenLoading} from "../../components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} ProjectsEditScreenProps */

/**
 * @typedef {object} ProjectsEditScreenState
 * @property {string} defaultOrganizationId Default organization id for new projects.
 * @property {boolean} loading Whether form data is loading.
 * @property {Project | null} project Loaded project when editing.
 * @property {boolean} saving Whether the form is saving.
 * @property {{kind: "error" | "success", text: string} | null} status Form status message.
 * @property {boolean} automationEnabled Whether AI task assignment is enabled for the project.
 */

/**
 * Creates or edits project records.
 * @augments {ShapeComponent<ProjectsEditScreenProps, ProjectsEditScreenState>}
 */
class ProjectsEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string | null} */
  projectID
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {ProjectsEditScreenState} */
  state = {
    defaultOrganizationId: "",
    loading: true,
    project: /** @type {Project | null} */ null,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    automationEnabled: false
  }

  /** Initializes translations, route helpers, and form data loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const projectID = optionalRouteParam(params, "id", "Project ID")
    this.params = params
    this.projectID = projectID
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const defaultOrganization = await Organization
            .query()
            .select(["id", "name"])
            .order([["name", "asc"], ["id", "asc"]])
            .limit(1)
            .first()
          const defaultOrganizationId = defaultOrganization
            ? String(defaultOrganization.id())
            : ""

          if (projectID) {
            const project = await Project.find(projectID)
            if (cancelled) return
            this.s.defaultOrganizationId = defaultOrganizationId
            this.s.loading = false
            this.s.project = project
            this.s.automationEnabled = Boolean(project.automationEnabled())
          } else {
            this.s.defaultOrganizationId = defaultOrganizationId
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [projectID])
  }

  /**
   * Renders the project form.
   * @returns {ReactNode} Project edit form content.
   */
  render() {
    const {automationEnabled, defaultOrganizationId, loading, project, saving, status} = this.s
    const {projectID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="projectEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectEditScreen"
      >
        <Text
          style={styles[`screenTitle-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="projectEditScreen/title"
        >
          {projectID ? _("Edit project") : _("New project")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCardSpacing ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="projectEditScreen/form"
          >
            <FormTextInput
              defaultValue={project ? formatDateInputValue(project.deadlineAt()) : ""}
              label={_("Deadline at")}
              name="deadlineAt"
              testID="projectEditScreen/deadlineAtInput"
            />
            <FormTextInput
              defaultValue={project ? project.name() || "" : ""}
              label={_("Name")}
              name="name"
              testID="projectEditScreen/nameInput"
            />
            <FormTextInput
              defaultValue={formatMoneyInputValue(project?.pricePerHourCents())}
              keyboardType="numeric"
              label={_("Price per hour")}
              name="pricePerHour"
              testID="projectEditScreen/pricePerHourInput"
            />
            <FormTextInput
              defaultValue={formatMoneyInputValue(project?.pricePerHourTransportCents())}
              keyboardType="numeric"
              label={_("Price per hour (transport)")}
              name="pricePerHourTransport"
              testID="projectEditScreen/pricePerHourTransportInput"
            />
            <OrganizationsSelect
              defaultValue={
                project?.organizationId()
                  ? String(project.organizationId())
                  : defaultOrganizationId || undefined
              }
              label={_("Organization")}
              name="organizationId"
              testID="projectEditScreen/organizationInput"
            />
            <FormSelect
              defaultValue={project?.state() || "active"}
              label={_("State")}
              name="state"
              options={projectStateOptions()}
              testID="projectEditScreen/stateInput"
            />
            <View
              style={styles.automationEnabledRow ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8
              }}
              testID="projectEditScreen/aiAssignmentRow"
            >
              <Switch
                onValueChange={this.tt.onAutomationEnabledChange}
                testID="projectEditScreen/aiAssignmentSwitch"
                value={automationEnabled}
              />
              <Text
                style={styles[`aiAssignmentLabel-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.text,
                  fontSize: 14
                }}
                testID="projectEditScreen/aiAssignmentLabel"
              >
                {_("Enable AI task assignment")}
              </Text>
            </View>
            <FormTextInput
              defaultValue={project?.automationEnvironmentId() || ""}
              label={_("Automation environment ID")}
              name="automationEnvironmentId"
              testID="projectEditScreen/aiEnvironmentInput"
            />
            {project &&
              <Text
                style={styles[`automationSetupStatus-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600
                }}
                testID="projectEditScreen/automationSetupStatus"
              >
                {_("Automation setup: %{status}", {
                  status: translatedAutomationSetupStatus(project.automationSetupStatus())
                })}
              </Text>
            }
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontWeight: 600
                }}
                testID="projectEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="projectEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Toggles whether the project opts into AI task assignment.
   * @param {boolean} automationEnabled New AI-assignment enabled state.
   * @returns {void} Nothing.
   */
  onAutomationEnabledChange = (automationEnabled) => {
    this.s.automationEnabled = automationEnabled
    this.s.status = null
  }

  /**
   * Saves the project form.
   * @returns {Promise<void>} Resolves when save handling finishes.
   */
  onSubmit = async () => {
    const {params, projectID, router} = this.tt
    const {defaultOrganizationId, project} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      const selectedOrganizationId =
        typeof values.organizationId === "string" && values.organizationId
          ? values.organizationId
          : project?.organizationId()
            ? String(project.organizationId())
            : defaultOrganizationId
      const organizationID = selectedOrganizationId || null
      const attributes = {
        deadlineAt:
          typeof values.deadlineAt === "string" && values.deadlineAt
            ? values.deadlineAt
            : null,
        name: values.name || "",
        organizationId: organizationID,
        pricePerHourCents: parseMoneyInputToCents(values.pricePerHour),
        pricePerHourCurrency: "DKK",
        pricePerHourTransportCents: parseMoneyInputToCents(
          values.pricePerHourTransport
        ),
        pricePerHourTransportCurrency: "DKK",
        state:
          typeof values.state === "string" && values.state
            ? values.state
            : "active",
        automationEnabled: this.s.automationEnabled,
        automationEnvironmentId:
          typeof values.automationEnvironmentId === "string" && values.automationEnvironmentId.trim()
            ? values.automationEnvironmentId.trim()
            : null
      }

      if (project && projectID) {
        await project.update(attributes)
        router.push(buildRoutePath(params, `/projects/${projectID}`))
      } else {
        const created = await Project.create(attributes)
        router.push(buildRoutePath(params, `/projects/${created.id()}`))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<ProjectsEditScreenProps>} */
const ProjectsEditScreenComponent = memo(shapeComponent(ProjectsEditScreen))

export default ProjectsEditScreenComponent

/**
 * Builds project state select options.
 * @returns {{text: string, value: string}[]} Labels and values for the state select.
 */
function projectStateOptions() {
  return [
    {text: _("Active"), value: "active"},
    {text: _("Inactive"), value: "inactive"},
    {text: _("Finished"), value: "finished"}
  ]
}

/**
 * Formats a date for the form input.
 * @param {Date | null | undefined} value Date value to format.
 * @returns {string} ISO date input value.
 */
function formatDateInputValue(value) {
  if (!value) return ""
  return new Date(value).toISOString().slice(0, 10)
}

/**
 * Formats cents as decimal kroner.
 * @param {number | null | undefined} cents Amount in cents.
 * @returns {string} Decimal money input value.
 */
function formatMoneyInputValue(cents) {
  if (cents == null) return ""
  return (cents / 100).toFixed(2)
}

/**
 * Parses a decimal money input into cents.
 * @param {unknown} value Raw form value.
 * @returns {number} Parsed amount in cents.
 * @throws {Error} When the amount is invalid.
 */
function parseMoneyInputToCents(value) {
  if (typeof value !== "string" || !value.trim()) return 0

  const normalizedValue = value.trim().replace(",", ".")

  if (!/^\d+(\.\d{1,2})?$/.test(normalizedValue)) {
    throw new Error(_("Invalid amount"))
  }

  return Math.round(Number.parseFloat(normalizedValue) * 100)
}

/**
 * Translates the public project automation setup status.
 * @param {string | null | undefined} status Setup-state code from the Project model.
 * @returns {string} Localized setup-state label.
 */
function translatedAutomationSetupStatus(status) {
  if (status === "complete") return _("Complete")
  if (status === "environment_required") return _("Environment required")
  if (status === "disabled") return _("Disabled")

  return _("Unknown")
}
